import { Text } from '@ui';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import messages from './PaymentCard.messages';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import { MastercardSVG } from '../../../svgComponents/mastercardSVG';
import { TickCircleSVG } from '../../../svgComponents/tickCircleSVG';
import { VisaSVG } from '../../../svgComponents/visaSVG';
import { EDIT_DEBIT_CARD } from '../../modals/editPaymentMethodModal/EditPaymentMethodModal.actions';
import { deserializePaymentMethod } from '../ProfileHelper';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    editDebitCard: (id: string) => {
        dispatch({
            type: EDIT_DEBIT_CARD,
            paymentMethodID: id
        });
    }
});

export function PaymentCardComponent({
    cardNumber,
    isPrimary,
    isWithStripe,
    paymentMethodID,
    brand,
    editDebitCard
}: {
    editDebitCard: (id: string) => void;
} & Pick<
    ReturnType<typeof deserializePaymentMethod>,
    | 'cardNumber'
    | 'isPrimary'
    | 'isWithStripe'
    | 'paymentMethodID'
    | 'brand'
    | 'isDisabled'
>) {
    const brandStyles: Record<string, string> = {
        visa: 'bg-[#00afdf]',
        mastercard: 'bg-[#252525]'
    };

    return (
        <div
            className="h-fit rounded-s bg-structure-100 shadow-black-s"
            data-testid="repayment-card"
        >
            <div
                className={classNames(
                    'flex min-h-[90px] items-end justify-between rounded-t-s p-l',
                    brandStyles[brand]
                )}
            >
                <Text className="text-s font-reg text-structure-100">
                    {`●●●● ${cardNumber?.slice(cardNumber?.length - 4)}`}
                </Text>
                <BrandIcon brand={brand} />
            </div>
            <div className="flex justify-between px-l py-s">
                <RepaymentLabel
                    isPrimary={isPrimary}
                    isWithStripe={isWithStripe}
                />
                <EditLabel
                    paymentMethodID={paymentMethodID}
                    isWithStripe={isWithStripe}
                    editDebitCard={editDebitCard}
                />
            </div>
        </div>
    );
}

function BrandIcon({ brand }: { brand: string }) {
    switch (brand) {
        case 'visa':
            return <VisaSVG className="h-auto max-w-[50px]" />;
        case 'mastercard':
            return <MastercardSVG className="h-auto max-w-[50px]" />;
        default:
            return null;
    }
}

function RepaymentLabel({
    isPrimary,
    isWithStripe
}: {
    isPrimary?: boolean;
    isWithStripe?: boolean;
}) {
    let message;
    if (getCountry() === 'DE' && isWithStripe) {
        message = <FormattedMessage {...messages.directDebit} />;
    } else if (isPrimary) {
        message = <FormattedMessage {...messages.automaticRepayment} />;
    }

    return message ? (
        <div className="flex items-center">
            {isPrimary && <TickCircleSVG />}
            <Text className="ml-s text-s font-med">{message}</Text>
        </div>
    ) : null;
}

function EditLabel({
    paymentMethodID,
    isWithStripe,
    editDebitCard
}: {
    paymentMethodID: string;
    isWithStripe: boolean;
    editDebitCard: (id: string) => void;
}) {
    // Can't edit direct debit details for DE
    if (getCountry() === 'DE' && isWithStripe) return null;

    return (
        <button
            className="ml-auto border-none bg-transparent underline hover:cursor-pointer"
            onClick={() => editDebitCard(paymentMethodID)}
        >
            <FormattedMessage {...messages.edit} />
        </button>
    );
}

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(PaymentCardComponent);
